/**
 * Heatmap application dictionary.
 * Stores all sources of truth regarding data structure and nomenclature.
 */

/** Dependencies -------------------------------------------- */

// External

import Cookies from 'js-cookie'

// Internal

import { ucFirst } from '../helpers'

/** Main ---------------------------------------------------- */

/**
 * Boundary level nomenclature.
 */
export const stateBoundaryLevel = 'adm1'

/**
 * Map config.
 */
export const mapInitialBounds = [ [ -124.848974, 49.384358 ], [ -66.885444, 24.396308 ] ]
export const mapMaxBounds = [ [ -151.785584, -0.063076 ], [ -43.439166, 64.145054 ] ]
export const mapMaxZoom = 6.66

/**
 * The development type dictionary.
 * Stores all information related to development types and their nomenclature.
 */
export const developmentTypes = [
    {
        id      : 'developmentTypeAdministration',
        label   : 'Administration',
        iconType: 'developmentTypeAdministration'
    },
    {
        id      : 'developmentTypeInformation',
        label   : 'Mis/Disinformation',
        iconType: 'developmentTypeInformation'
    },
    {
        id      : 'developmentTypeInterference',
        label   : 'Interference',
        iconType: 'developmentTypeInterference'
    },
    {
        id      : 'developmentTypeViolence',
        label   : 'Violence',
        iconType: 'developmentTypeViolence'
    }
]

/**
 * The development level dictionary.
 * Stores all information related to development levels and their nomenclature.
 */
export const developmentLevels = [
    {
        id          : 'developmentLevel0',
        label       : 'Level 0',
        numericValue: 0
    },
    {
        id          : 'developmentLevel1',
        label       : 'Level 1',
        numericValue: 1
    },
    {
        id          : 'developmentLevel2',
        label       : 'Level 2',
        numericValue: 2
    },
    {
        id          : 'developmentLevel3',
        label       : 'Level 3',
        numericValue: 3
    }
]

/**
 * Preamble acceptance cookie config.
 */
const preambleAcceptanceCookieKey = 'wasPreambleAccepted'
const preambleAcceptanceCookieExpiration = 7

/** Getters ------------------------------------------------- */

/**
 * Gets values from the various dictionaries based on their ids.
 */
export const getDevelopmentType = lookup( developmentTypes )
export const getDevelopmentLevel = lookup( developmentLevels )

/**
 * Gets plugin configuration.
 */
export function getPlugin() { return electionTaskforceHeatmap }

/**
 * Gets the value of the preamble acceptance cookie.
 */
export function getPreambleAcceptanceCookie() {
    return !! Cookies.get( `${getPlugin().jsSlug}${ucFirst( preambleAcceptanceCookieKey )}` )
}

/** Setters & Normalizers ----------------------------------- */

/**
 * Normalizes an aggregate from the various sources into props for use in the UI.
 */
export function normalizeAggregate( aggregate ) {
    return {
        ...aggregate,
        developmentLevel: getDevelopmentLevel( aggregate.developmentLevel )
    }
}

/**
 * Normalizes a development from the various sources into props for use in the UI.
 */
export function normalizeDevelopment( development ) {
    return {
        ...development,
        created    : parseInt( development.created ),
        updated    : parseInt( development.updated ),
        developmentType : getDevelopmentType( development.developmentType ),
        developmentLevel: getDevelopmentLevel( development.developmentLevel )
    }
}

/**
 * Sets the value of the preamble acceptance cookie.
 */
export function setPreambleAcceptanceCookie( value = true ) {
    Cookies.set( `${getPlugin().jsSlug}${ucFirst( preambleAcceptanceCookieKey )}`, value, { expires: preambleAcceptanceCookieExpiration } )
}

/** Utilities ----------------------------------------------- */

/**
 * General lookup function.
 * Takes a dictionary as an argument, then returns a function to look up entries by id.
 */
function lookup( dictionary ) {
    return ( entryId ) => dictionary.find( ( entry ) => entry.id  === entryId )
}